'use client';

import { useEffect, useActionState, useTransition } from 'react';

import { Button } from '@/vibes/soul/primitives/button';
import { toast } from '@/vibes/soul/primitives/toaster';
import { useEvents } from '~/components/analytics/events';
import { useRouter } from '~/i18n/routing';

import { addToCartSimple } from '../../../../app/[locale]/(default)/_actions/add-to-cart-simple';

interface Props {
  productId: string;
  productHref?: string;
  addToCartLabel?: string;
  outOfStockLabel?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger';
  className?: string;
  isInStock?: boolean;
  inventoryMessage?: string;
}

export function AddToCartForm({
  productId,
  productHref,
  addToCartLabel = 'Add to cart',
  disabled = false,
  size = 'small',
  variant = 'primary',
  className,
  isInStock = true,
  inventoryMessage,
}: Props) {
  const events = useEvents();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  
  const [{ lastResult, successMessage }, formAction] = useActionState(addToCartSimple, {
    lastResult: null,
  });
  
  const getButtonLabel = () => {
    return inventoryMessage || addToCartLabel;
  };

  const shouldLinkToPdp = Boolean(inventoryMessage && productHref && productHref !== '#');

  const handleSubmit = (formData: FormData) => {
    if (shouldLinkToPdp && productHref) {
      router.push(productHref);

      return;
    }

    if (!isInStock) return;
    
    startTransition(() => {
      formAction(formData);
      events.onAddToCart?.(formData);
    });
  };

  // Handle success/error messages
  useEffect(() => {
    if (lastResult?.status === 'success' && successMessage) {
      toast.success(successMessage);
    }
    
    if (lastResult?.status === 'error') {
      toast.error(lastResult.error);
    }
  }, [lastResult, successMessage]);

  return (
    <form action={handleSubmit} className={className}>
      <input name="id" type="hidden" value={productId} />
      <input name="quantity" type="hidden" value="1" />
      <Button
        type="submit"
        disabled={disabled || (!isInStock && !shouldLinkToPdp)}
        loading={isPending}
        size={size}
        variant={variant}
        className="w-full"
        shape="square"
      >
        {getButtonLabel()}
      </Button>
    </form>
  );
}
